import { useMemo, useState, useCallback } from "react"
import { useAppSelector, useInfiniteScroll } from "../../common/hooks"
import ActivityTable from "../../common/ui/activity/table"
import { DEFAULT_SELECTED_TYPES } from "../../common/ui/activity/filter"
import { DateRangePreset } from "../../common/ui/activity/date-filter"
import { hasUserRole, dateRangeToTimestamps } from "../../common/utils"
import { useFetchActivityFeedNewQuery } from "../../redux/queries"
import { selectUserDetails, selectUserRoles } from "../../redux/slices/auth"
import { PageHeader } from "../../common/ui"

const ITEMS_PER_PAGE = 75

const RecentActivity = () => {
  const userDetails = useAppSelector(selectUserDetails)
  const userRoles = useAppSelector(selectUserRoles)
  const [hasMore, setHasMore] = useState(true)
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(
    DEFAULT_SELECTED_TYPES
  )
  const [selectedDateRange, setSelectedDateRange] =
    useState<DateRangePreset>("all")
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState<string | null>(
    null
  )

  const isBrandAdmin = hasUserRole(userRoles, "bank admin")
  const isSuperAdmin = hasUserRole(userRoles, "administrator")
  const isAdmin = isBrandAdmin || isSuperAdmin
  const isLo = hasUserRole(userRoles, "loan officer")
  const orgId = userDetails?.org?.id

  // Calculate timestamp filters from date range
  const { min: startTimestamp, max: endTimestamp } = useMemo(
    () =>
      dateRangeToTimestamps(selectedDateRange, customStartDate, customEndDate, {
        unit: "milliseconds"
      }),
    [selectedDateRange, customStartDate, customEndDate]
  )

  const [queryParams, setQueryParams] = useState(() => {
    const params: {
      loid?: number
      brand_id?: number
      org_id?: number
      limit: number
      offset: number
      metrics?: string
      timestamp_min?: number
      timestamp_max?: number
    } = {
      limit: ITEMS_PER_PAGE,
      offset: 0
    }

    if (isAdmin) {
      // For admins (brand admin or super admin), use org_id to get activity from entire org
      if (userDetails?.org?.id) {
        const parsedOrgId = parseInt(userDetails.org.id, 10)
        if (!isNaN(parsedOrgId)) {
          params.org_id = parsedOrgId
        }
      }
    } else if (isLo && userDetails?.id) {
      // Only add loid for loan officers who are NOT brand admins
      const parsedLoid = parseInt(userDetails.id, 10)
      if (!isNaN(parsedLoid)) {
        params.loid = parsedLoid
      }
    }

    return params
  })

  // Update query params when event filter changes
  useMemo(() => {
    if (selectedEventTypes.length > 0) {
      setQueryParams(prev => ({
        ...prev,
        metrics: selectedEventTypes.join(","),
        offset: 0 // Reset pagination when filter changes
      }))
      setHasMore(true)
    } else {
      setQueryParams(prev => {
        const { metrics, ...rest } = prev
        return {
          ...rest,
          offset: 0 // Reset pagination when filter changes
        }
      })
      setHasMore(true)
    }
  }, [selectedEventTypes])

  // Update query params when date filter changes
  useMemo(() => {
    setQueryParams(prev => {
      const updated = { ...prev, offset: 0 }

      if (startTimestamp !== undefined && endTimestamp !== undefined) {
        // Convert milliseconds to seconds for API
        updated.timestamp_min = Math.floor(startTimestamp / 1000)
        updated.timestamp_max = Math.floor(endTimestamp / 1000)
      } else {
        delete updated.timestamp_min
        delete updated.timestamp_max
      }

      return updated
    })
    setHasMore(true)
  }, [startTimestamp, endTimestamp])

  // Update query params when loan officer filter changes (admin only)
  useMemo(() => {
    if (!isAdmin) return

    setQueryParams(prev => {
      const updated = { ...prev, offset: 0 }

      if (selectedLoanOfficer) {
        const parsedLoid = parseInt(selectedLoanOfficer, 10)
        if (!isNaN(parsedLoid)) {
          updated.loid = parsedLoid
        }
      } else {
        delete updated.loid
      }

      return updated
    })
    setHasMore(true)
  }, [selectedLoanOfficer, isAdmin])

  // Super admins can view the global activity feed (no org_id/loid needed)
  const shouldSkip =
    (!isAdmin && !isLo) ||
    (!isSuperAdmin && !queryParams.org_id && !queryParams.loid)

  const {
    data: activities,
    isFetching,
    refetch
  } = useFetchActivityFeedNewQuery(queryParams, {
    skip: shouldSkip
  })

  const data = useMemo(() => {
    return (activities ?? []).map(activity => ({
      ...activity,
      isLoActivity: false
    }))
  }, [activities])

  // Check if we have more data
  useMemo(() => {
    if (activities && !isFetching) {
      // Calculate how many items we should have after the current page
      const expectedMinimum = queryParams.offset + ITEMS_PER_PAGE

      // If we have fewer items than expected, we've reached the end
      if (activities.length < expectedMinimum) {
        setHasMore(false)
      } else {
        // If we have exactly the expected amount or more, there might be more data
        setHasMore(true)
      }
    }
  }, [activities, isFetching, queryParams.offset])

  // Load more callback for infinite scroll
  const loadMore = useCallback(() => {
    if (!activities || activities.length === 0 || isFetching || !hasMore) return

    // Increment offset by page size
    setQueryParams(prev => ({
      ...prev,
      offset: prev.offset + ITEMS_PER_PAGE
    }))
  }, [activities, isFetching, hasMore])

  // Handle manual refetch (reset pagination)
  const handleRefetch = useCallback(() => {
    setHasMore(true)
    setQueryParams(prev => ({
      ...prev,
      offset: 0
    }))
    refetch()
  }, [refetch])

  // Handle event filter changes
  const handleFilterChange = useCallback((types: string[]) => {
    setSelectedEventTypes(types)
  }, [])

  const handleClearFilters = useCallback(() => {
    setSelectedEventTypes([])
  }, [])

  // Handle date filter changes
  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      setSelectedDateRange(range)
      if (range === "custom" && startDate && endDate) {
        setCustomStartDate(startDate)
        setCustomEndDate(endDate)
      }
    },
    []
  )

  const handleClearDateFilter = useCallback(() => {
    setSelectedDateRange("all")
    setCustomStartDate(null)
    setCustomEndDate(null)
  }, [])

  // Handle loan officer filter changes (brand admin only)
  const handleLoanOfficerChange = useCallback((loid: string | null) => {
    setSelectedLoanOfficer(loid)
  }, [])

  // Infinite scroll observer ref
  const loadMoreRef = useInfiniteScroll({
    hasMore,
    isLoading: isFetching,
    onLoadMore: loadMore,
    rootMargin: "200px"
  })

  return (
    <div className="h-full p-6 bg-white">
      <PageHeader title="Recent activity" />

      <div className="mt-4">
        <ActivityTable
          data={data}
          isLoading={isFetching && queryParams.offset === 0}
          refetch={handleRefetch}
          loadMoreRef={loadMoreRef}
          isLoadingMore={isFetching && queryParams.offset > 0}
          hasMore={hasMore}
          selectedEventTypes={selectedEventTypes}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          selectedDateRange={selectedDateRange}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onDateRangeChange={handleDateRangeChange}
          onClearDateFilter={handleClearDateFilter}
          isBrandAdmin={isAdmin}
          orgId={orgId}
          selectedLoanOfficer={selectedLoanOfficer}
          onLoanOfficerChange={handleLoanOfficerChange}
        />
      </div>
    </div>
  )
}

export default RecentActivity
